import React, { useEffect, useRef } from "react";
import { Bot } from "lucide-react";

/** Lightweight, safe markdown renderer for assistant bubbles.
 *
 * Supports a *very* small subset chosen to match the response-formatter
 * directive: `## Heading`, `• ` / `- ` bullets, `**bold**`, blank-line paragraphs.
 * No raw HTML, no links, no images — strings are escaped first.
 */
function escapeHtml(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderMarkdownLite(text) {
  const safe = escapeHtml(text || "");
  const lines = safe.split(/\r?\n/);
  const out = [];
  let inList = false;
  for (let raw of lines) {
    const line = raw.trimEnd();
    if (/^##\s+/.test(line)) {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<h4 class="rch-md-h">${line.replace(/^##\s+/, "")}</h4>`);
    } else if (/^\s*[•\-]\s+/.test(line)) {
      if (!inList) { out.push('<ul class="rch-md-ul">'); inList = true; }
      const item = line.replace(/^\s*[•\-]\s+/, "");
      out.push(`<li>${item}</li>`);
    } else if (line === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push('<div class="rch-md-br"></div>');
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p class="rch-md-p">${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  // Inline **bold** transform (post-line so HTML is already structurally safe).
  return out.join("").replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
}

/**
 * Claude v2.4-inspired message list — asymmetric bubble corners, avatar on
 * assistant rows, gradient bubbles for user, and an animated typing indicator.
 *
 * Pure presentational; messages array shape: { role: "assistant" | "user", content: string }.
 */
export default function ChatMessages({ messages, sending, logoUrl, hero }) {
  const endRef = useRef(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const fullLogo = logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : "";

  return (
    <div className="rch-msgs" data-testid="chat-messages">
      {hero}
      {messages.map((m, i) => (
        <div className={`rch-row ${m.role === "user" ? "user" : ""}`} key={i}>
          {m.role !== "user" && (
            <div className="rch-avatar-sm" aria-hidden>
              {fullLogo ? <img src={fullLogo} alt="" /> : <Bot className="w-4 h-4 text-white" />}
            </div>
          )}
          {m.role === "user" ? (
            <div className="rch-bubble user">{m.content}</div>
          ) : (
            <div
              className="rch-bubble rch-md"
              // markdown-lite is escaped-first; safe by construction.
              dangerouslySetInnerHTML={{ __html: renderMarkdownLite(m.content) }}
            />
          )}
        </div>
      ))}
      {sending && (
        <div className="rch-row" data-testid="typing-indicator">
          <div className="rch-avatar-sm" aria-hidden>
            {fullLogo ? <img src={fullLogo} alt="" /> : <Bot className="w-4 h-4 text-white" />}
          </div>
          <div className="rch-typing"><span /><span /><span /></div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
